// Dev-only blocker fixtures + their dispatcher, split out of `mocks.ts` on the
// `dashboard-mocks` precedent. This is the gap `apps/dashboard/CLAUDE.md` had
// written down and accepted ("no e2e story walks the rail today, so nothing goes
// red") — which is exactly the shape that stays broken: the screen rendered
// `<ErrorState/>` under `PUBLIC_MOCKS` and no test could tell.
//
// THE ANSWER TEXT IS NEVER SERVED BACK. `maskBlocker` omits it and answers
// `answered: boolean` instead, and this fixture does the same — the field a human
// types into is the one most likely to receive a pasted credential, and a mock
// that echoed it would be the one place in the app that reads one back.
//
// STATEFUL, because both writes CLOSE the row and the route re-reads after each.
// A stateless echo would re-serve the open blocker on the next GET and let the
// story answer the same ask for ever.

import type { ApiBlocker, BlockerAnswerWire } from './blockers';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/** An open prose decision on `task_id`: answered in words, with no pick and no form. */
function prose(
  id: string,
  task_id: string,
  summary: string,
  answer_files_ask: boolean,
): ApiBlocker {
  return {
    id,
    kind: 'decision_needed',
    summary,
    detail: summary,
    repo_full_name: 'developerz-ai/developerz.ai',
    run_id: null,
    task_id,
    runner_id: null,
    asked_at_iso: at('2026-07-18'),
    resolution: null,
    answered_at_iso: null,
    answered_by_user_id: null,
    answered: false,
    raw_task_id: null,
    needs: null,
    options: null,
    guidance: null,
    answer_files_ask,
    fix_url: null,
  };
}

/**
 * The answer route's arm per row (#6726, `apps/api/src/routes/blockers.ts` `answerChanged`):
 * `released` for a held task's blocker, `linked` for one tied to a live task. Every other
 * row files an ask for triage.
 */
const MOCK_ARM: Readonly<Record<string, 'released' | 'linked'>> = {
  blk_mock05: 'released',
  blk_mock06: 'linked',
};

/**
 * The three kinds, and the two `needs`/`fix_url` states that matter: a
 * `missing_credential` carrying a needs form gets the prefilled link, everything
 * else is answerable in PROSE and says so rather than reading as a failure.
 */
const blockers: ApiBlocker[] = [
  {
    id: 'blk_mock01',
    kind: 'missing_credential',
    summary: 'The staging registry needs a pull token',
    detail: 'The build pulls base images from registry.internal and has no credential for it.',
    repo_full_name: 'developerz-ai/api-gateway',
    run_id: 'run_mock01',
    task_id: 'tsk_mock_live',
    runner_id: 'run_mock01',
    asked_at_iso: at('2026-07-14'),
    // `resolution: null` IS the open state, and the domain refuses any other
    // combination on the read — so the four closed-only fields are all null/false.
    resolution: null,
    answered_at_iso: null,
    answered_by_user_id: null,
    answered: false,
    raw_task_id: null,
    // One field of every v2 kind, two already landed — so the card, and the
    // repo view's access panel for `api-gateway`, render supplied beside
    // outstanding and the OAuth field's Connect.
    needs: {
      version: 2,
      fields: [
        { kind: 'env_secret', name: 'REGISTRY_USER', environment: 'ci' },
        {
          kind: 'env_secret',
          name: 'REGISTRY_TOKEN',
          environment: 'ci',
          description: 'Pull token for registry.internal, read-only scope.',
        },
        { kind: 'service_url', name: 'METRICS_URL', environment: 'dev' },
        { kind: 'mcp_server', name: 'sentry', transport: 'sse', token_ref: 'sentry-token' },
        { kind: 'oauth_connection', provider: 'sentry', scopes: ['event:read'] },
      ],
      landed: ['REGISTRY_USER', 'mcp:sentry'],
    },
    options: null,
    // The sweep's own candidate shape (#6679): open, no choices, so it carries STEPS.
    // Plain language and specific to the row, which is what the prompt asks for.
    guidance: {
      v: 1,
      kind: 'steps',
      steps: [
        'Open the repository on GitHub, then Settings, then Secrets and variables, then Actions.',
        'Press New repository secret and type the name REGISTRY_USER exactly, in capitals.',
        'Paste the registry username as the value, then press Add secret.',
        'Come back here and say you have added it, so the work can start again.',
      ],
      free_label: 'Say which name you used',
      model: 'glm-5.2',
      generated_at: at('2026-07-14'),
    },
    answer_files_ask: true,
    fix_url: 'https://app.example/fix/blk_mock01?sig=devmock',
  },
  {
    id: 'blk_mock02',
    kind: 'decision_needed',
    summary: 'Drop Node 18 support, or keep the polyfill?',
    detail: 'The dependency bump needs Node 20. Keeping 18 means carrying a polyfill nobody owns.',
    repo_full_name: 'developerz-ai/api-gateway',
    run_id: null,
    task_id: 'tsk_mock_done',
    runner_id: null,
    asked_at_iso: at('2026-07-16'),
    resolution: null,
    answered_at_iso: null,
    answered_by_user_id: null,
    answered: false,
    raw_task_id: null,
    // No form and no keys — answerable in prose, which the screen must SAY.
    needs: null,
    // The question is a choice, so it offers the choices (#6387): radios + "Other".
    options: {
      version: 1,
      choices: [
        { key: 'drop_node18', label: 'Drop Node 18 support', note_required: false },
        { key: 'keep_polyfill', label: 'Keep the polyfill', note_required: false },
      ],
    },
    // A row that already offers CHOICES is answered by picking one, so the sweep leaves
    // it alone and this stays null (#6679).
    guidance: null,
    // A TERMINAL linked task, so answering still files an ask (#6535).
    answer_files_ask: true,
    fix_url: null,
  },
  // Three questions asked in prose (#6726): `options` and `needs` null, each on a task a
  // Fleet fixture machine holds (`fleet-machine-mocks.ts`), so its Mission Panel card reads
  // Needs you and offers an admin "Answer here". One per arm of the answer route: the first
  // files an ask for triage, the second's task is HELD and is released, the third is linked
  // to a live task that carries on ({@link MOCK_ARM}). The last two file nothing, which is
  // `answer_files_ask: false` (#6535).
  prose(
    'blk_mock04',
    'tsk_mock_parked',
    'Ship the changelog as one page, or one per release?',
    true,
  ),
  prose('blk_mock05', 'tsk_mock_held', 'Which staging region should the smoke test use?', false),
  prose('blk_mock06', 'tsk_mock_linked', 'Keep the old flag name as an alias?', false),
  {
    // Closed, so `?open=false` has something to widen to and the two orders
    // (oldest-first open, newest-first closed) are both observable.
    id: 'blk_mock03',
    kind: 'unreachable_service',
    summary: 'The VPN-fenced metrics host is unreachable from the box',
    detail: 'metrics.internal:9090 refuses every connection from the runner subnet.',
    repo_full_name: 'developerz-ai/docs-site',
    run_id: null,
    task_id: null,
    runner_id: 'run_mock02',
    asked_at_iso: at('2026-06-30'),
    resolution: 'answered',
    answered_at_iso: at('2026-07-01'),
    answered_by_user_id: 'usr_mock01',
    answered: true,
    raw_task_id: 'rtk_mock01',
    needs: null,
    options: null,
    // Closed before the column existed: the sweep only ever reads OPEN rows (#6679).
    guidance: null,
    answer_files_ask: true,
    fix_url: null,
  },
];

/**
 * Close a row in place, so the re-read after the write sees stored truth. `filed` is whether
 * the answer filed an ask (the triage arm); a released or linked answer files none.
 */
function close(
  id: string,
  resolution: 'answered' | 'withdrawn',
  filed = resolution === 'answered',
): ApiBlocker | null {
  const i = blockers.findIndex((b) => b.id === id && b.resolution === null);
  const row = blockers[i];
  if (row === undefined) return null;
  const answered = resolution === 'answered';
  const closed: ApiBlocker = {
    ...row,
    resolution,
    answered_at_iso: answered ? at('2026-07-20') : null,
    answered_by_user_id: answered ? 'usr_mock01' : null,
    // Whether words were supplied — never the words.
    answered,
    raw_task_id: filed ? `rtk_${id}` : null,
    // A closed row has no live form to sign. `guidance` is NOT reset: the steps a person
    // followed are why the row closed, and the store does not clear them either.
    options: null,
    answer_files_ask: filed || !answered,
    fix_url: null,
  };
  blockers[i] = closed;
  return closed;
}

/**
 * Handle a `/v1/blockers*` request, or return `null` if `path` is not a blocker
 * route (so `mockFetch` falls through). A second answer REJECTS rather than
 * doubling — the API refuses it 409, and that refusal is a branch the screen
 * renders.
 */
export function blockersMock(path: string, method: string): Promise<unknown> | null {
  const [bare = path, query = ''] = path.split('?');
  if (bare !== '/v1/blockers' && !bare.startsWith('/v1/blockers/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  if (bare === '/v1/blockers') {
    if (method !== 'GET') return reject();
    // `?open=` is the literal string `true`/`false` and defaults to open — the
    // route's own spelling. Keyed `blockers`; this list carries NO `next_cursor`
    // field at all, so inventing one would advertise paging that does not exist.
    const open = new URLSearchParams(query).get('open') !== 'false';
    const rows = blockers.filter((b) => (b.resolution === null) === open);
    return Promise.resolve({ blockers: rows });
  }

  const [id = '', action = ''] = bare.slice('/v1/blockers/'.length).split('/');
  if (method !== 'POST') return reject();
  if (action === 'answer') return answerMock(id, reject);
  if (action === 'withdraw') {
    const row = close(id, 'withdrawn');
    return row === null ? reject() : Promise.resolve({ blocker: row });
  }
  return reject();
}

/**
 * An answer's 200, by the row's arm (#6726): the triage arm files a raw task carrying the
 * ask and the answer; a held task's answer releases it (`released_task`, the route's
 * `heldTaskReleasedJson`); a linked one names the live task (`linkedAnswerJson`).
 */
function answerMock(id: string, reject: () => Promise<never>): Promise<unknown> {
  const arm = MOCK_ARM[id];
  const row = close(id, 'answered', arm === undefined);
  if (row === null) return reject();
  const task_id = row.task_id ?? `tsk_${id}`;
  const body: BlockerAnswerWire =
    arm === 'released'
      ? {
          blocker: row,
          released_task: {
            task_id,
            status: 'pending',
            prior_attempt: 1,
            released_dependents: [],
            saved_wip: { found: false },
          },
        }
      : arm === 'linked'
        ? { kind: 'linked', blocker: row, task_id }
        : { blocker: row, raw_task_id: `rtk_${id}` };
  return Promise.resolve(body);
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_BLOCKERS = blockers.map((row) => structuredClone(row));

export function resetBlockersMocks(): void {
  blockers.splice(0, blockers.length, ...PRISTINE_BLOCKERS.map((row) => structuredClone(row)));
}
